import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableConnectorPageFilterApplied,
  fetchAllConnectors,
  fetchConnectorPageFilters,
  setConnectorLimit,
  setConnectorPage,
  setConnectorSortBy,
  setConnectorSortField,
  updateConnectorPageFilters,
} from '../../../redux/slices/ConnectorSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import deviceApi from '../../../utils/api/device-hub';
import AddConnectorRatingDrawer from '../../ev-device-hub/components/drawers/AddConnectorRatingDrawer';
import EditConnectorRatingDrawer from '../../ev-device-hub/components/drawers/EditConnectorRatingDrawer';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Local connector management component — displays connectors belonging to current tenant.
 * @returns {React.JSX.Element} Local connector component
 */
function Local() {
  const { t } = useTranslation(['deviceHub', 'sharedComponent']);
  const dispatch = useAppDispatch();
  const {
    connectors,
    loading,
    totalResults,
    totalPages,
    page,
    limit,
    filters,
    isFilterUpdated,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.connector);

  const [ratingDrawerOpen, setRatingDrawerOpen] = useState(false);
  const [editRatingOpen, setEditRatingOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState<any>(null);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: null,
    header: false,
    content: null,
    notificationModal: false,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllConnectors({
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
        ...queryParameters,
      }),
    );
  }, []);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllConnectors({
          page: DefaultApiLimit.page,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableConnectorPageFilterApplied());
    }
  }, [isFilterUpdated]);

  /**
   * Handles sort change from CustomTable
   * @param {any} sortData - Sort data payload
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setConnectorSortBy(sortData.sortBy));
    dispatch(setConnectorSortField(sortData.sortField));
    dispatch(
      fetchAllConnectors({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and limit changes
   * @param {any} value - Page and limit data
   * @param {any} value.page - The page prop
   * @param {any} value.limit - The limit prop
   */
  const handlePageLimitChange = (value: { page?: number; limit?: number }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (value.page !== undefined) dispatch(setConnectorPage(value.page));
    if (value.limit !== undefined) dispatch(setConnectorLimit(value.limit));
    dispatch(
      fetchAllConnectors({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes connector data using current filters
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllConnectors({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Handles confirm delete rating.
   * @param {string} ratingId - The ID of the rating to delete.
   */
  const handleConfirmDeleteRating = (ratingId: string) => {
    deviceApi
      .deleteConnectorRating(ratingId)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title={t('connectorRatingDeletedSuccess')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                handleRefresh();
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        setModalBoxStatus('close');
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            message: error?.response?.data?.message || t('somethingWentWrong'),
            code: error?.response?.data?.code || error?.response?.status || 500,
          }),
        );
      });
  };

  /**
   * Handles delete rating click.
   * @param {string} ratingId - The ID of the rating to delete.
   */
  const handleDeleteRatingClick = (ratingId: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={ratingId}
          title={t('deleteRatingConfirm')}
          description={t('actionUndoneWarning')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(id) => id && handleConfirmDeleteRating(id)}
        />
      ),
      notificationModal: true,
    });
  };

  const column = [
    { 
      label: t('connectorName'),
      accessorKey: 'name',
      sortAllow: true,
      /**
       * Renders name
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => <span>{currentRow.name || '-'}</span>,
    },
    {
      label: t('currentType'),
      accessorKey: 'currentType',
      /**
       * Renders connector type
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span className="current-type-label">{currentRow.currentType?.toUpperCase() || '-'}</span>
      ),
    },
    {
      label: t('maxPowerOutputKw'),
      accessorKey: 'capacityKW',
      sortAllow: true,
      /**
       * Renders max power output
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span>{currentRow.capacityKW ? `${currentRow.capacityKW}` : '-'}</span>
      ),
    },
    {
      label: t('deviceCount'),
      accessorKey: 'deviceCount',
      sortAllow: true,
      /**
       * Renders device count
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => <span>{currentRow.deviceCount || 0}</span>,
    },
    {
      label: t('action'),
      accessorKey: 'action',
      sortAllow: false,
      columnWidth: 80,
      sticky: 'right' as const,
      /**
       * Renders action
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <div className="action-buttons-wrap">
          {permissionCheck('devicesRatingAdd') && (
            <>
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedRating(currentRow);
                  setEditRatingOpen(true);
                }}>
                <EditIcon className="icon edit" />
              </div>
              <div
                role="presentation"
                className="action-button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteRatingClick(currentRow._id);
                }}>
                <DeleteIcon className="icon delete" />
              </div>
            </>
          )}
        </div>
      ),
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    {
      label: t('connectorName'),
      accessorKey: 'name',
    },
    {
      label: t('currentType'),
      accessorKey: 'currentType',
    },
    {
      label: t('maxPowerOutputKw'),
      accessorKey: 'capacityKW',
    },
    {
      label: t('deviceCount'),
      accessorKey: 'deviceCount',
    },
    {
      label: t('createdAt', { defaultValue: 'Created At' }),
      accessorKey: 'createdAt',
    },
    {
      label: t('updatedAt', { defaultValue: 'Last Updated' }),
      accessorKey: 'updatedAt',
    },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function to export data in PDF or CSV format
   * @param {string} type - The export format type ('csv' or 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: Record<string, any> = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    deviceApi
      .exportConnectorRatings(removeEmptyKeys(payload))
      .then((response: any) => {
        const exportDataList = Array.isArray(response?.data)
          ? response.data
          : response?.data?.results || response?.results || [];

        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = exportDataList.map((row: any, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return String(index + 1);

              case 'name':
                return row?.name || 'N/A';

              case 'currentType':
                return row?.currentType ? row.currentType.toUpperCase() : 'N/A';

              case 'capacityKW':
                return row?.capacityKW !== undefined && row?.capacityKW !== null
                  ? String(row.capacityKW)
                  : 'N/A';

              case 'deviceCount':
                return row?.deviceCount !== undefined && row?.deviceCount !== null
                  ? String(row.deviceCount)
                  : '0';

              case 'createdAt':
                return row?.createdAt ? ConvertUTCtoDateTime(row.createdAt) : 'N/A';

              case 'updatedAt':
                return row?.updatedAt ? ConvertUTCtoDateTime(row.updatedAt) : 'N/A';

              default:
                return row?.[col.accessorKey] ?? 'N/A';
            }
          }),
        );

        setExportLoader(false);

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'connector-ratings.csv');
          } else {
            exportPDF(headers, rows, 'connector-ratings.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('errorMessage', 'Something went wrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code || error?.response?.status || 500,
          }),
        );
      });
  };

  return (
    <>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        header={modalBoxDetail.header}
        content={modalBoxDetail.content}
        notification={modalBoxDetail.notificationModal}
        modalBoxAnimation="modalAnimateToLeft"
        closeModal={() => setModalBoxStatus('close')}
      />
      <CustomTable
        dataKey="ConnectorManagement"
        header={column}
        data={connectors}
        serialNo
        version="v2"
        loading={loading}
        page={page}
        limit={limit}
        height={310}
        created
        lastUpdated
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={handlePageLimitChange}
        sizeChangeButtonRequired
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        filters={filters}
        filtersToDispatch={updateConnectorPageFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConnectorPageFilters}
        actionButton
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('refresh')}
        refershCurrentPages={handleRefresh}
        onHiddenColumnsChange={setHiddenColumns}
        rightTopContent={
          <div className="table-bottom-button-list">
            {permissionCheck('devicesRatingAdd') && (
              <div className="table-button-item">
                <MyButton
                  label={t('addConnectorRatingLabel')}
                  buttonType="submit"
                  onClickFunc={() => setRatingDrawerOpen(true)}
                />
              </div>
            )}
          </div>
        }
      />

      <AddConnectorRatingDrawer
        open={ratingDrawerOpen}
        onClose={() => setRatingDrawerOpen(false)}
        existingValues={connectors.map((c: any) => c.name || c._id)}
        onRatingCreated={() => {
          setRatingDrawerOpen(false);
          handleRefresh();
        }}
      />

      <EditConnectorRatingDrawer
        open={editRatingOpen}
        ratingData={selectedRating}
        onClose={() => {
          setEditRatingOpen(false);
          setSelectedRating(null);
        }}
        onRatingUpdated={() => {
          setEditRatingOpen(false);
          setSelectedRating(null);
          handleRefresh();
        }}
      />
    </>
  );
}

export default Local;
